/// <reference types="vite/client" />

/**
 * Deploy-resilient chunk recovery.
 *
 * When a new version ships while a tab is open, that tab's already-loaded app
 * still references the OLD hashed JS chunks. The moment it lazy-loads a route
 * (on navigation, or on hover via `defaultPreload: 'intent'`) it fetches a
 * chunk whose content hash no longer exists on the server → the dynamic import
 * rejects. Unhandled, that rejection blanks the screen — the "white screen
 * after deploy" report.
 *
 * Vite fires a `vite:preloadError` window event the instant a module preload
 * fails — the earliest, most reliable signal, well before the failure could
 * bubble through React's render cycle to {@link RouteError}. We catch it here
 * (plus an `unhandledrejection` fallback) and do a single, guarded full reload,
 * which re-fetches index.html → the fresh asset manifest → the correct chunk
 * hashes. Auth survives (the access token re-mints from the httpOnly refresh
 * cookie on boot), so the user lands right back where they were, updated.
 *
 * The guard is time-based so a genuinely-broken chunk can't reload-loop: after
 * one reload we won't reload again for {@link RELOAD_COOLDOWN_MS}; a route that
 * is still broken then falls through to the calm RouteError panel instead of
 * spinning.
 */

const RELOAD_FLAG = 'projoint:chunk-reloaded-at';
const RELOAD_COOLDOWN_MS = 20_000;

/** True when an error looks like a failed dynamic-import / stale chunk load. */
export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /ChunkLoadError|dynamically imported module|Loading chunk|Importing a module script failed|Failed to fetch dynamically|error loading dynamically imported/i.test(
    msg,
  );
}

/**
 * Reload once to pick up the freshly-deployed asset manifest. Returns `true`
 * if a reload was triggered, `false` if suppressed by the cooldown — so callers
 * can render a fallback UI instead of assuming a reload is coming.
 */
export function reloadForStaleChunk(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
  } catch {
    // sessionStorage can throw in private mode / sandboxed contexts — treat as
    // "never reloaded" and proceed; a reload is still the right move.
  }
  const now = Date.now();
  if (now - last < RELOAD_COOLDOWN_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch {
    /* ignore — see above */
  }
  window.location.reload();
  return true;
}

/**
 * Wire the earliest stale-chunk signals to a guarded reload. Call once at
 * bootstrap (production only), before React renders, so a preload failure
 * self-heals before the user ever sees a blank.
 */
export function setupChunkRecovery(): void {
  // Vite's own signal — fires the moment a modulepreload / dynamic import
  // fails. preventDefault stops Vite from re-throwing it as an uncaught error.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForStaleChunk();
  });

  // Belt-and-suspenders: some browsers surface the failed import as an
  // unhandled rejection rather than (or as well as) the Vite event.
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadForStaleChunk();
    }
  });
}
